/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react'
import useSupercluster from 'use-supercluster'
import ReactMapGL, { Marker, Popup, NavigationControl } from 'react-map-gl'
import {
  Center,
  City,
  SetState,
  ViewState,
} from '../../../types/AddressesGroup'

interface MapProps {
  centers: Center[]
  viewState: ViewState
  setViewState: SetState<ViewState>
  isCorporation: boolean
  setCentersInList: SetState<Center[] | []>
  isTopCfe: boolean
  mapRef: any
  setSelectedCity: SetState<City | undefined>
  waitForMvt: boolean
  setWaitForMvt: SetState<boolean>
  MAPBOX_API_KEY: string
  MAPBOX_API_STYLE: string
  isTouch: boolean
}

const AddressesMap: React.FC<MapProps> = ({
  centers,
  viewState,
  setViewState,
  isCorporation,
  setCentersInList,
  isTopCfe,
  mapRef,
  setSelectedCity,
  waitForMvt,
  setWaitForMvt,
  MAPBOX_API_KEY,
  MAPBOX_API_STYLE,
}: MapProps) => {
  const [selectedCenter, setSelectedCenter] = useState<any>({})
  const initialBounds = [
    -5.329084882814328, 40.99372866185908, 9.529678152619908,
    51.686975244551206,
  ]
  // points
  const points = centers.map((center: Center) => ({
    type: 'Feature',
    properties: {
      cluster: false,
      centerId: center.id,
      price: center.price,
      minPrice: center.min_price_auto_entrepreneur_asso,
      image: center.index_image,
      shortname: center.shortname,
      postalCity: center.city,
      lng: center.lng,
      lat: center.lat,
      slug: center.slug,
    },
    geometry: { type: 'Point', coordinates: [center.lng, center.lat] },
  }))

  // bounds for clusters
  const boundsForClusters = mapRef.current
    ? mapRef.current.getMap().getBounds().toArray().flat()
    : null

  // cluster (markers)
  const { clusters, supercluster } = useSupercluster({
    points,
    zoom: viewState.zoom,
    bounds: boundsForClusters ? boundsForClusters : initialBounds,
    options: { radius: 70, maxZoom: 20 },
  })

  const checkIfPositionInViewport = (lat: number, lng: number) => {
    if (mapRef.current) {
      const bounds = mapRef.current.getMap().getBounds()
      return (
        lat >= bounds._sw.lat &&
        lat <= bounds._ne.lat &&
        lng >= bounds._sw.lng &&
        lng <= bounds._ne.lng
      )
    } else {
      return (
        lat >= initialBounds[1] &&
        lat <= initialBounds[3] &&
        lng >= initialBounds[0] &&
        lng <= initialBounds[2]
      )
    }
  }
  useEffect(() => {
    let centersInViewport = []
    if (isTopCfe) {
      centersInViewport = centers.filter((center: Center) => {
        return (
          checkIfPositionInViewport(center.lat, center.lng) && center.top_cfe
        )
      })
    } else {
      centersInViewport = centers.filter((center: Center) => {
        return checkIfPositionInViewport(center.lat, center.lng)
      })
    }
    mapRef.current?.on('move', (e: any) => {
      if (e.originalEvent) {
        setSelectedCity(undefined)
      }
    })
    if (waitForMvt) {
      setTimeout(() => {
        setWaitForMvt(false)
      }, 1500)
    } else {
      setCentersInList(centersInViewport)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewState, isTopCfe, centers])
  return (
    <div className="sticky-addresses-map">
      <ReactMapGL
        ref={mapRef}
        {...viewState}
        onMove={evt => setViewState(evt.viewState)}
        mapStyle={MAPBOX_API_STYLE}
        mapboxAccessToken={MAPBOX_API_KEY}
        dragRotate={false}
        touchZoomRotate={false}
        style={{ height: '100vh', width: '100%' }}
      >
        <>
          <NavigationControl
            showZoom
            showCompass={false}
            position={'top-left'}
          />

          {clusters.map(cluster => {
            const [lng, lat] = cluster.geometry.coordinates
            const {
              cluster: isCluster,
              point_count: pointCount,
              price: price,
              minPrice: minPrice,
              image: image,
              sector_name: sector_name,
              shortname: shortname,
              slug: slug,
            } = cluster.properties
            if (isCluster) {
              return (
                <Marker key={cluster.id} latitude={lat} longitude={lng}>
                  <div
                    className="cluster-marker has-text-weight-semibold has-text-heading is-size-10"
                    style={{
                      width: `${10 + (pointCount / points.length) * 100}px`,
                      minWidth: '40px',
                      height: `${10 + (pointCount / points.length) * 100}px`,
                      minHeight: '40px',
                    }}
                    onClick={() => {
                      const expansionZoom = Math.min(
                        supercluster.getClusterExpansionZoom(cluster.id),
                        20
                      )
                      mapRef.current?.flyTo({
                        center: [lng, lat],
                        zoom: expansionZoom,
                        duration: 2000,
                      })
                    }}
                  >
                    {pointCount}
                  </div>
                </Marker>
              )
            }
            return (
              <div key={cluster.properties.centerId}>
                <Marker latitude={lat} longitude={lng}>
                  <div
                    className={`button addresses-button is-medium has-text-weight-semibold ${
                      selectedCenter.centerId === cluster.properties.centerId
                        ? 'bg-btn-map'
                        : 'btn-addresses-map'
                    }`}
                    onClick={() => setSelectedCenter(cluster.properties)}
                  >
                    {isCorporation ? price : minPrice} €
                  </div>
                </Marker>

                {selectedCenter.lng === lng && selectedCenter.lat === lat && (
                  <Popup
                    latitude={lat}
                    longitude={lng}
                    onClose={() => setSelectedCenter({})}
                    closeOnClick
                    offset={25}
                  >
                    <img
                      src={image.url}
                      alt="photo du centre"
                      className="map-center-image"
                    />
                    <div className="py-2 px-3">
                      <div className="is-size-body1 has-text-heading has-text-weight-bold mb-1">
                        {shortname}
                      </div>
                      <div className="is-size-body1 has-text-heading">
                        {sector_name}
                      </div>
                      <a
                        className="map-simple-link is-size-9 neutral-button"
                        href={`${window.location.pathname}/${slug}`}
                      >
                        Voir le centre
                      </a>
                    </div>
                  </Popup>
                )}
              </div>
            )
          })}
        </>
      </ReactMapGL>
    </div>
  )
}

export default AddressesMap
